import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Button,
  Col,
  Flex,
  LegacyCard,
  Row,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { bnplMerchantApis } from "@payping/api";
import { ActivationSteps } from "@payping/api/app/api/bnplMerchantTypes";
import { bnplMerchantGetContract } from "@payping/redux/actions";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import BnplActivationPageForm from "./Form";
import { URLS } from "@payping/utilities-shared/consts";
import { useImmerAtom } from "jotai-immer";
import { installmentPaymentAtom } from "../state";

const BnplActivationPage = () => {
  const { t } = useAppTranslation();
  const listItems = t("shop.bnpl.activation.intro.card.list.items", { returnObjects: true }) as string[];
  const { breakpoints } = usePaypingContext();
  const { sm, md } = breakpoints;
  const contract = useAppSelector((state) => state.bnplMerchant.contract);
  const dispatch = useAppDispatch();
  const [openFormDrawer, setOpenFormDrawer] = useState<boolean>(false);
  const navigate = useNavigate();

  const [, setInstallmentPaymentState] = useImmerAtom(installmentPaymentAtom);

  const [createContractState, createContract] = useMessagedAsyncFn(
    () => bnplMerchantApis.createContract(undefined),
    [],
  );
  const [setCreditState, setCredit] = useMessagedAsyncFn(
    (contractId: number) => bnplMerchantApis.setCredit({ contractId }),
    [],
  );

  useEffect(() => {
    if (contract?.next) {
      setInstallmentPaymentState((draft) => {
        // eslint-disable-next-line no-param-reassign
        draft.bnplActivationStep = contract?.next;
      });
    }
    if (
      contract?.next === ActivationSteps.WaitingForAdmin ||
      contract?.next === ActivationSteps.SignContract ||
      contract?.next === ActivationSteps.NoNext
    ) {
      navigate(DashboardRoutes.myShop.bnpl.manage.root.url);
    }
  }, [contract?.next, dispatch, navigate, setInstallmentPaymentState]);

  const handleOpenFormDrawer = () => {
    setOpenFormDrawer(true);
  };

  const handleCLoseFormDrawer = () => {
    setOpenFormDrawer(false);
  };

  const handleStart = async () => {
    // does not have contract
    if (contract?.next === null || contract?.next === undefined || contract?.next === ActivationSteps.CreateContract) {
      const res = await createContract();
      if (res?.contractId) {
        await setCredit(res?.contractId);
        setInstallmentPaymentState((draft) => {
          // eslint-disable-next-line no-param-reassign
          draft.bnplActivationStep = ActivationSteps.SetWalletCreditAmount;
        });

        handleOpenFormDrawer();
        return;
      }
    }

    // have contract
    if (contract?.next === ActivationSteps.SetCredit && contract?.contractId) {
      await setCredit(contract.contractId);
      setInstallmentPaymentState((draft) => {
        // eslint-disable-next-line no-param-reassign
        draft.bnplActivationStep = ActivationSteps.SetWalletCreditAmount;
      });

      handleOpenFormDrawer();
      return;
    }
    handleOpenFormDrawer();
  };

  const handleDoneActivation = async () => {
    await dispatch(bnplMerchantGetContract());
  };

  const isLoadingStart = createContractState.loading || setCreditState.loading;

  return (
    <>
      <Flex vertical className={classNames(["tw:mt-18! tw:w-full", { "tw:p-4!": !md, "tw:pr-6!": md }])}>
        <Flex className="tw:mb-24! tw:w-full" align="center" vertical gap={"large"}>
          {contract?.next === null ||
          contract?.next === undefined ||
          contract?.next === ActivationSteps.CreateContract ? (
            <>
              <Typography.Title level={5} className="tw:m-0!">
                {t("shop.bnpl.activation.intro.title")}
              </Typography.Title>
              <Typography.Text>{t("shop.bnpl.activation.intro.content")}</Typography.Text>
              <Button loading={isLoadingStart} onClick={handleStart} type="primary">
                {t("shop.bnpl.activation.intro.start")}
              </Button>
            </>
          ) : (
            <>
              <Typography.Title level={5} className="tw:m-0!">
                {t("shop.bnpl.activation.notActivatedIntro.title")}
              </Typography.Title>
              <Typography.Text>{t("shop.bnpl.activation.notActivatedIntro.content")}</Typography.Text>
              <Button loading={isLoadingStart} onClick={handleStart} type="primary">
                {t("shop.bnpl.activation.notActivatedIntro.start")}
              </Button>
            </>
          )}
        </Flex>
        <Flex className="tw:w-full" justify="center">
          <LegacyCard.Ant
            title={
              <Flex align="center" justify="space-between">
                <Typography.Text>
                  <strong>{t("shop.bnpl.activation.intro.card.title")}</strong>
                </Typography.Text>
              </Flex>
            }
            type="inner"
            className="tw:w-full tw:max-w-219"
          >
            <Row gutter={[16, 16]} className="tw:w-full">
              <Col md={10} sm={22} xs={24}>
                <Flex vertical className="tw:w-full" gap={"middle"}>
                  <Flex vertical className="tw:w-full">
                    <Typography.Text>{t("shop.bnpl.activation.intro.card.content")}</Typography.Text>
                    <Typography.Text>{t("shop.bnpl.activation.intro.card.list.title")}</Typography.Text>
                  </Flex>
                  <Flex
                    align="center"
                    className={classNames([
                      {
                        "tw:w-full": md,
                        "tw:w-full tw:max-w-62.5": sm,
                      },
                    ])}
                    justify="space-between"
                  >
                    <ul className="tw:m-0! tw:p-0! tw:pr-4!">
                      <li>
                        <Typography.Text>{listItems[0]}</Typography.Text>
                      </li>
                      <li>
                        <Typography.Text>{listItems[1]}</Typography.Text>
                      </li>
                      <li>
                        <Typography.Text>{listItems[2]}</Typography.Text>
                      </li>
                    </ul>
                    <ul className="tw:m-0! tw:p-0! tw:pr-4!">
                      <li>
                        <Typography.Text>{listItems[3]}</Typography.Text>
                      </li>
                      <li>
                        <Typography.Text>{listItems[4]}</Typography.Text>
                      </li>
                      <li>
                        <Typography.Text>{listItems[5]}</Typography.Text>
                      </li>
                    </ul>
                  </Flex>
                </Flex>
              </Col>
              {sm ? <Col md={6}></Col> : null}
              <Col md={8} sm={24}>
                <Flex className="tw:w-full" justify={md ? "flex-end" : "center"}>
                  <img
                    className="tw:h-44.25 tw:w-62.5 tw:object-cover"
                    src={`${URLS.STORAGE_URL}/dashboard-app/installment-payment/installment-payment-activation-intro.png`}
                  />
                </Flex>
              </Col>
            </Row>
          </LegacyCard.Ant>
        </Flex>
      </Flex>
      <BnplActivationPageForm open={openFormDrawer} onClose={handleCLoseFormDrawer} onDone={handleDoneActivation} />
    </>
  );
};

export default BnplActivationPage;
